import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Chip,
    Drawer,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { CREATE, DataAPIContext, GET_ONE, UPDATE } from '../api';
import DeleteIconButton from '../components/DeleteIconButton';
import ImageActionButton from '../components/ImageActionButton';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import imagesAPI from '../components/images';
import LicenseeFormField from '../components/LicenseeFormField';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import useLeavePrompt, { LEAVE_WITHOUT_SAVING_MESSAGE } from '../components/useLeavePrompt';
import { ContextStore } from '../store/contextStore';

const PREVIEW_W = 358;
const PREVIEW_H = 200;

// The user-editable part of the form, which the leave prompt compares.
const formSnapshot = (form, imageFile) => ({
    name: form.name,
    license_plate: form.license_plate,
    type_id: form.type?.id ?? form.type_id ?? null,
    fleet_mgmt_id: form.fleet_mgmt?.id ?? form.fleet_mgmt_id ?? null,
    route_set: form.route_set?.id ?? null,
    route_id: form.route_id ?? null,
    tour_operator: toLicenseeId(form.tour_operator),
    imageChanged: imageFile !== undefined,
});

const VehicleEditDialog = ({ open, vehicleId, onClose }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { settings, isFranchise, currentLicensee, hasPerm } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('vehicle_fleet', 'write');
    const { enqueueSnackbar } = useSnackbar();

    const isEdit = vehicleId !== null;
    const [loading, setLoading] = React.useState(false);
    // imageSrc: string (blob URL) | {p, s} object | null
    // imageFile: undefined (no change) | File (new upload) | null (clear)
    const [imageSrc, setImageSrc] = React.useState(null);
    const [imageFile, setImageFile] = React.useState(undefined);
    const [form, setForm] = React.useState({
        name: '',
        license_plate: '',
        // Status is read-only — populated from the backend, not user-editable.
        status: null,
        status_ts: null,
        enabled: true,
        fleet_mgmt_id: null,
        fleet_mgmt: null,
        type_id: null,
        type: null,
        route_set: null,
        route_id: null,
        route: null,
        route_hints: null,
        route_assignment_ts: null,
        last_sync_ts: null,
        tour_operator: null,
    });

    const [loadedForm, setLoadedForm] = React.useState(null);

    const hasChanges = open && hasWritePerm && loadedForm !== null && !isEqual(formSnapshot(form, imageFile), loadedForm);
    const leaveWithoutPrompt = useLeavePrompt(hasChanges);

    // X, CANCEL and the backdrop. Closing only changes state here (the list
    // swaps the URL afterwards), so this asks itself rather than through the
    // router; the router prompt covers leaving the page with the drawer open.
    const requestClose = () => {
        if (hasChanges && !window.confirm(LEAVE_WITHOUT_SAVING_MESSAGE)) return;
        leaveWithoutPrompt(() => onClose(false));
    };

    const onLicenseeChange = (id) => {
        setForm((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    React.useEffect(() => {
        if (open && isEdit) {
            setLoadedForm(null);
            setLoading(true);
            dataAPI(GET_ONE, 'vehicle', { id: vehicleId })
                .then((res) => {
                    const data = res.data || {};
                    const loaded = {
                        name: data.name || '',
                        license_plate: data.license_plate || '',
                        status: data.status || null,
                        status_ts: data.status_ts || null,
                        enabled: data.enabled,
                        fleet_mgmt_id: data.fleet_mgmt_id,
                        fleet_mgmt: data.fleet_mgmt_id ? { id: data.fleet_mgmt_id, name: data.fleet_mgmt_name } : null,
                        type_id: data.type_id,
                        type: data.type_id ? { id: data.type_id, name: data.type_name } : null,
                        route_set: data.route_set,
                        route_id: data.route?.id || null,
                        route: data.route,
                        route_hints: data.route_hints || null,
                        route_assignment_ts: data.route_assignment_ts || null,
                        last_sync_ts: data.last_sync_ts || null,
                        tour_operator: data.tour_operator ?? null,
                    };
                    setForm(loaded);
                    setImageSrc(data.image || null);
                    setImageFile(undefined);
                    setLoadedForm(formSnapshot(loaded, undefined));
                })
                .catch(() => enqueueSnackbar('Error loading vehicle', { variant: 'error' }))
                .finally(() => setLoading(false));
        } else if (open) {
            const blank = {
                name: '',
                license_plate: '',
                status: null,
                status_ts: null,
                enabled: true,
                fleet_mgmt_id: null,
                fleet_mgmt: null,
                type_id: null,
                type: null,
                route_set: null,
                route_id: null,
                route: null,
                route_hints: null,
                route_assignment_ts: null,
                last_sync_ts: null,
                tour_operator: currentLicensee ?? null,
            };
            setForm(blank);
            setImageSrc(null);
            setImageFile(undefined);
            setLoadedForm(formSnapshot(blank, undefined));
        }
    }, [open, vehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

    const onRouteSetChange = (ev, value) => {
        setForm((prev) => ({
            ...prev,
            route_set: value,
            // Clear the previously selected route since it likely no longer belongs
            // to the newly selected line set.
            route_id: null,
            route: null,
        }));
    };

    const onRouteChange = (ev, value) => {
        setForm((prev) => ({
            ...prev,
            route_id: value?.id || null,
            route: value,
        }));
    };

    const onPickImage = (ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImageSrc(URL.createObjectURL(file));
        }
    };

    const clearImage = () => {
        setImageFile(null);
        setImageSrc(null);
    };

    const renderedImageSrc = React.useMemo(() => {
        if (!imageSrc) return null;
        if (typeof imageSrc === 'string') return imageSrc;
        return imagesAPI.url(settings?.images?.base, imageSrc, `cover_${PREVIEW_W}x${PREVIEW_H}`);
    }, [imageSrc, settings]);

    const onSave = () => {
        const body = {
            name: form.name,
            license_plate: form.license_plate,
            fleet_mgmt_id: form.fleet_mgmt?.id || form.fleet_mgmt_id,
            type_id: form.type?.id || form.type_id,
            route_id: form.route_id,
            tour_operator: form.tour_operator,
            ...(imageFile === null ? { image_id: null } : {}),
        };

        if (!body.fleet_mgmt_id || !body.type_id) {
            enqueueSnackbar('Vehicle type and fleet management system are required', { variant: 'warning' });
            return;
        }

        setLoading(true);
        const action = isEdit ? UPDATE : CREATE;
        const hasFile = imageFile instanceof File;
        const params = {
            ...(isEdit ? { id: vehicleId } : {}),
            data: body,
            ...(hasFile ? { files: { image: imageFile }, upload: true } : {}),
        };

        dataAPI(action, 'vehicle', params)
            .then((res) => {
                if (res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.data?.message || 'Error saving vehicle', { variant: 'error' });
                } else {
                    enqueueSnackbar(isEdit ? 'Vehicle updated' : 'Vehicle created', { variant: 'default' });
                    leaveWithoutPrompt(() => onClose(true));
                }
            })
            .catch(() => enqueueSnackbar('Error saving vehicle', { variant: 'error' }))
            .finally(() => setLoading(false));
    };

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(form.tour_operator)}>
        <Drawer anchor="right" open={open} onClose={requestClose} PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, overflowY: 'auto' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 2 }}>
                    <Typography variant="h6">{isEdit ? 'Edit bus' : 'Create New Bus'}</Typography>
                    <IconButton onClick={requestClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <LicenseeFormField value={form.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                    <TextField
                        variant="filled"
                        label="Bus ID / Name"
                        value={form.name}
                        onChange={(e) => { const { value } = e.target; setForm((prev) => ({ ...prev, name: value })); }}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        variant="filled"
                        label="Licence Plate"
                        value={form.license_plate}
                        onChange={(e) => { const { value } = e.target; setForm((prev) => ({ ...prev, license_plate: value })); }}
                        fullWidth
                        size="small"
                    />

                    {isEdit && (
                        <Box sx={{ py: 1 }}>
                            <Typography variant="body2" color="text.secondary">Status</Typography>
                            <Typography variant="body1">
                                {form.status || '—'}
                                {form.status_ts && (
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                        (updated {(() => {
                                            const d = new Date(form.status_ts);
                                            if (Number.isNaN(d.getTime())) return form.status_ts;
                                            return d.toLocaleString(undefined, {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            });
                                        })()})
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                    )}

                    {isEdit && Array.isArray(form.route_hints) && form.route_hints.length > 0 && (
                        <Box sx={{ py: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Route hints</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {form.route_hints.map((hint, i) => (
                                    <Chip key={i} label={hint} size="small" variant="outlined" />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {isEdit && (form.route_assignment_ts || form.last_sync_ts) && (
                        <Box sx={{ py: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Sync info</Typography>
                            {form.route_assignment_ts && (
                                <Typography variant="body2">
                                    Route assigned: {(() => {
                                        const d = new Date(form.route_assignment_ts);
                                        if (Number.isNaN(d.getTime())) return form.route_assignment_ts;
                                        return d.toLocaleString(undefined, {
                                            year: 'numeric', month: 'short', day: 'numeric',
                                            hour: '2-digit', minute: '2-digit',
                                        });
                                    })()}
                                </Typography>
                            )}
                            {form.last_sync_ts && (
                                <Typography variant="body2">
                                    Last inspected: {(() => {
                                        const d = new Date(form.last_sync_ts);
                                        if (Number.isNaN(d.getTime())) return form.last_sync_ts;
                                        return d.toLocaleString(undefined, {
                                            year: 'numeric', month: 'short', day: 'numeric',
                                            hour: '2-digit', minute: '2-digit',
                                        });
                                    })()}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <RemoteAutocomplete
                        resource="vehicle_type"
                        label="Vehicle Type"
                        variant="filled"
                        size="small"
                        value={form.type}
                        urlSuffix={null}
                        onChange={(ev, value) => setForm((prev) => ({ ...prev, type: value, type_id: value?.id || null }))}
                        getOptionLabel={(opt) => opt?.name || ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    />

                    <RemoteAutocomplete
                        resource="fleet_management"
                        label="Fleet Management System"
                        variant="filled"
                        size="small"
                        value={form.fleet_mgmt}
                        urlSuffix={null}
                        onChange={(ev, value) => setForm((prev) => ({ ...prev, fleet_mgmt: value, fleet_mgmt_id: value?.id || null }))}
                        getOptionLabel={(opt) => opt?.name || ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    />

                    <RemoteAutocomplete
                        resource="route_set"
                        label="Assign to line set"
                        variant="filled"
                        size="small"
                        value={form.route_set}
                        urlSuffix={null}
                        onChange={onRouteSetChange}
                        getOptionLabel={(opt) => opt?.name || ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        optional
                    />

                    <RemoteAutocomplete
                        resource="route"
                        label="Assign to line"
                        variant="filled"
                        size="small"
                        value={form.route}
                        urlSuffix={null}
                        onChange={onRouteChange}
                        getOptionLabel={(opt) => opt?.name || ''}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        // Filter by the selected line set via the backend's
                        // ?route_set=<id> query param. refreshKey forces a refetch
                        // (and cache bust) when the user picks a different set.
                        apiParams={form.route_set?.id ? { route_set: form.route_set.id } : {}}
                        refreshKey={form.route_set?.id || 'none'}
                        optional
                        disabled={!form.route_set}
                    />

                    {renderedImageSrc ? (
                        <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                            <Image
                                src={renderedImageSrc}
                                cover
                                height={PREVIEW_H}
                                width="100%"
                                style={{ borderRadius: 8, display: 'block' }}
                            />
                            <Box sx={{ position: 'absolute', top: 11, right: 11 }}>
                                <DeleteIconButton title="Delete image" overImage allowed={hasWritePerm} onClick={clearImage} />
                            </Box>
                            <ImageActionButton
                                hasImage
                                overImage
                                component="label"
                                disabled={!hasWritePerm}
                                sx={{ position: 'absolute', bottom: 12, right: 12 }}
                            >
                                <input type="file" hidden accept="image/*" onChange={onPickImage} />
                            </ImageActionButton>
                        </Box>
                    ) : (
                        <ImageActionButton component="label" disabled={!hasWritePerm} sx={{ alignSelf: 'flex-start' }}>
                            <input type="file" hidden accept="image/*" onChange={onPickImage} />
                        </ImageActionButton>
                    )}
                </Box>

                <Box sx={{ borderTop: '1px solid #d8d8d8', p: 2, display: 'flex', gap: 1, position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                    <FormActionButtons
                        mode={isEdit ? 'edit' : 'create'}
                        onSubmit={onSave}
                        submitDisabled={loading || !form.name || (isEdit && !hasChanges)}
                        onCancel={requestClose}
                        size="medium"
                        sx={{ flex: 1 }}
                    />
                </Box>
            </Box>
        </Drawer>
        </FormLicenseeContext.Provider>
    );
};

export default VehicleEditDialog;
